import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";
import { ScenarioMap } from "./ScenarioMap";
import { ShareButton } from "./ShareButton";

const STEPS = [
  "Reading the homepage",
  "Reading the privacy and cookie policies",
  "Detecting jurisdictions",
  "Mapping frameworks",
  "Connecting the dots",
];

const TIPS = [
  "We read only what is already public, then turn it into a starting point you can correct.",
  "Larger organisations take a little longer, because there is more to map.",
  "Everything we show is indicative guidance, not legal advice.",
  "You will be able to correct anything just by chatting with the copilot.",
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Inline theatre while we map the scenario. Honest status lines, a live timer
// and an abstract scenario animation. On a warm cache hit the parent skips this.
export const AnalysingState = ({ company, searchedUrl }) => {
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [tip, setTip] = useState(0);

  useEffect(() => {
    const st = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 1200);
    const el = setInterval(() => setElapsed((s) => s + 1), 1000);
    const tp = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 3500);
    return () => { clearInterval(st); clearInterval(el); clearInterval(tp); };
  }, []);

  const who = company && company !== "your company" ? company : "your website";

  return (
    <section className="border-t hairline max-w-[1240px] mx-auto px-5 lg:px-8 py-14 lg:py-16" data-testid="analysing-state">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-[13px] font-medium text-mutedink">Mapping {who}</div>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold px-2.5 py-1 rounded-full bg-aqua-tint text-asphalt tabular-nums" data-testid="analysing-timer">
              <Loader2 size={12} className="animate-spin" /> {fmt(elapsed)}
            </span>
            {searchedUrl && <ShareButton domain={searchedUrl} className="ml-auto" />}
          </div>
          <h2 className="mt-2 text-3xl lg:text-4xl font-bold text-asphalt">Mapping your compliance scenario</h2>
          <p className="mt-3 text-mutedink">This usually takes about 20 seconds, and up to about 70 seconds for a large or first time site. Hang tight, it is worth the wait.</p>

          <ul className="mt-8 space-y-3.5" data-testid="analysing-steps">
            {STEPS.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <li key={s} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors" style={{ background: done ? "#2FE1EC" : active ? "#EEF2F6" : "#F5F6F8" }}>
                    {done ? <Check size={14} color="#364F6B" strokeWidth={3} /> : active ? <Loader2 size={14} className="animate-spin" color="#364F6B" /> : <span className="w-1.5 h-1.5 rounded-full bg-line" />}
                  </span>
                  <span className={`text-[15px] ${done || active ? "text-asphalt font-medium" : "text-mutedink"}`}>{s}</span>
                </li>
              );
            })}
          </ul>

          <div className="mt-8 rounded-lg border hairline bg-white px-4 py-3 text-[13.5px] text-mutedink leading-relaxed min-h-[52px] flex items-center" data-testid="analysing-tip">
            {TIPS[tip]}
          </div>

          {elapsed >= 30 && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg px-4 py-3 bg-aqua-tint border hairline" data-testid="slow-scan-note">
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#2FE1EC" }}><Sparkles size={13} color="#364F6B" /></span>
              <p className="text-[13.5px] text-asphalt leading-relaxed">Still gathering records for this one. Larger or first time sites take a little longer, hang tight, I am almost there.</p>
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
          <ScenarioMap height={340} />
        </motion.div>
      </div>
    </section>
  );
};
